import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { motion, AnimatePresence } from "framer-motion";
import {
  TrendingUp, Zap, Users, DollarSign, Clock, ChevronRight,
  Flame, Globe, Tv, Cpu, Music, Building2, Beaker, Vote,
  Loader2, RefreshCw, Sparkles, ArrowRight,
} from "lucide-react";
import { useState } from "react";

const CATEGORY_CONFIG: Record<string, { icon: typeof Flame; color: string; bg: string }> = {
  sports: { icon: Flame, color: "text-orange-400", bg: "bg-orange-400/10" },
  entertainment: { icon: Tv, color: "text-pink-400", bg: "bg-pink-400/10" },
  technology: { icon: Cpu, color: "text-neon", bg: "bg-neon/10" },
  social_media: { icon: Globe, color: "text-blue-400", bg: "bg-blue-400/10" },
  politics: { icon: Vote, color: "text-red-400", bg: "bg-red-400/10" },
  culture: { icon: Music, color: "text-purple-400", bg: "bg-purple-400/10" },
  business: { icon: Building2, color: "text-gold", bg: "bg-gold/10" },
  science: { icon: Beaker, color: "text-green-400", bg: "bg-green-400/10" },
};

const TIME_RELEVANCE_LABELS: Record<string, { label: string; color: string }> = {
  breaking: { label: "BREAKING", color: "bg-red-500/20 text-red-400 border-red-500/30" },
  today: { label: "TODAY", color: "bg-orange-500/20 text-orange-400 border-orange-500/30" },
  this_week: { label: "THIS WEEK", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  ongoing: { label: "ONGOING", color: "bg-green-500/20 text-green-400 border-green-500/30" },
};

interface TrendingEventsProps {
  onUseTemplate?: (template: any) => void;
  compact?: boolean;
}

export default function TrendingEvents({ onUseTemplate, compact = false }: TrendingEventsProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [expandedEvent, setExpandedEvent] = useState<number | null>(null);

  const { data, isLoading, refetch, isFetching } = trpc.trending.getEvents.useQuery(
    { category: selectedCategory as any, limit: compact ? 3 : 5 },
    { staleTime: 5 * 60 * 1000 } // Cache for 5 minutes
  );

  const categories = [
    { key: undefined, label: "All" },
    { key: "sports", label: "Sports" },
    { key: "entertainment", label: "Entertainment" },
    { key: "technology", label: "Tech" },
    { key: "social_media", label: "Social" },
    { key: "culture", label: "Culture" },
    { key: "business", label: "Business" },
    { key: "science", label: "Science" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold">Trending Now</h2>
            <p className="text-xs text-muted-foreground">
              Monetize viral moments with skill-based games
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground hover:text-neon"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          Refresh
        </Button>
      </div>

      {/* Category Filter */}
      {!compact && (
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <Button
              key={cat.key ?? "all"}
              variant={selectedCategory === cat.key ? "default" : "outline"}
              size="sm"
              className={`text-xs ${
                selectedCategory === cat.key
                  ? "bg-neon text-background"
                  : "border-border/50 hover:border-neon/30"
              }`}
              onClick={() => setSelectedCategory(cat.key)}
            >
              {cat.label}
            </Button>
          ))}
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="relative">
            <div className="w-12 h-12 rounded-full border-2 border-neon/20 border-t-teal animate-spin" />
            <Sparkles className="w-5 h-5 text-gold absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-sm text-muted-foreground">Scanning trending events...</p>
        </div>
      ) : data?.error ? (
        <Card className="glass-panel border-destructive/20">
          <CardContent className="pt-6 text-center">
            <p className="text-sm text-destructive">{data.error}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      ) : data?.events && data.events.length > 0 ? (
        <div className="space-y-4">
          <AnimatePresence>
            {data.events.map((event, idx) => {
              const catConfig = CATEGORY_CONFIG[event.category] || CATEGORY_CONFIG.culture;
              const timeConfig = TIME_RELEVANCE_LABELS[event.timeRelevance] || TIME_RELEVANCE_LABELS.ongoing;
              const CatIcon = catConfig.icon;
              const isExpanded = expandedEvent === idx;

              return (
                <motion.div
                  key={`${event.title}-${idx}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card
                    className={`glass-panel cursor-pointer transition-all duration-200 ${
                      isExpanded ? "border-neon/30" : "border-border/30 hover:border-neon/20"
                    }`}
                    onClick={() => setExpandedEvent(isExpanded ? null : idx)}
                  >
                    <CardContent className="pt-5 pb-4">
                      {/* Event Header */}
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-lg ${catConfig.bg} flex items-center justify-center shrink-0`}>
                          <CatIcon className={`w-4 h-4 ${catConfig.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="outline" className={`text-[10px] ${timeConfig.color}`}>
                              {timeConfig.label}
                            </Badge>
                            <Badge variant="outline" className="text-[10px] border-border/50 text-muted-foreground">
                              {event.source}
                            </Badge>
                          </div>
                          <h3 className="font-semibold text-sm leading-tight">{event.title}</h3>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {event.description}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="text-right">
                            <p className="text-[10px] text-muted-foreground uppercase">Viral</p>
                            <p className={`font-mono font-bold text-sm ${
                              event.viralPotential >= 80 ? "text-gold" :
                              event.viralPotential >= 60 ? "text-neon" :
                              "text-muted-foreground"
                            }`}>
                              {event.viralPotential}
                            </p>
                          </div>
                          <ChevronRight
                            className={`w-4 h-4 text-muted-foreground transition-transform ${
                              isExpanded ? "rotate-90" : ""
                            }`}
                          />
                        </div>
                      </div>

                      {/* Expanded: Game Templates */}
                      {isExpanded && event.suggestedGames && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          className="mt-4 space-y-3"
                        >
                          <Separator className="bg-border/30" />
                          <p className="text-xs text-neon font-medium uppercase tracking-wider flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3" />
                            Suggested Games
                          </p>
                          {event.suggestedGames.map((game, gIdx) => (
                            <div
                              key={gIdx}
                              className="p-3 rounded-lg bg-secondary/30 border border-border/30 space-y-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-semibold">{game.title}</h4>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {game.description}
                                  </p>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2 text-[10px]">
                                <span className="flex items-center gap-1 text-gold">
                                  <DollarSign className="w-3 h-3" />
                                  ${(game.entryFee / 100).toFixed(2)} entry
                                </span>
                                <span className="flex items-center gap-1 text-neon">
                                  <Users className="w-3 h-3" />
                                  {game.maxPlayers} players
                                </span>
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <Clock className="w-3 h-3" />
                                  {game.duration}
                                </span>
                              </div>
                              <p className="text-xs text-foreground/70">
                                <span className="text-gold font-medium">Viral hook:</span> {game.viralHook}
                              </p>
                              {onUseTemplate && (
                                <Button
                                  size="sm"
                                  className="w-full bg-neon hover:bg-neon/90 text-background gap-1.5 text-xs mt-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onUseTemplate({
                                      gameTemplate: game,
                                      eventTitle: event.title,
                                      eventDescription: event.description,
                                    });
                                  }}
                                >
                                  <Zap className="w-3 h-3" />
                                  Use This Template
                                  <ArrowRight className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      ) : (
        <Card className="glass-panel">
          <CardContent className="pt-6 text-center py-12">
            <TrendingUp className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No trending events found. Try refreshing.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
